const GATEWAY_TIMEOUT_STATUS = 504;

export const MAX_GATEWAY_TIMEOUT_RETRIES = 3;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getNumberValue = (value: unknown) =>
  typeof value === 'number' ? value : undefined;

const getStringValue = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

const hasGatewayTimeoutSignature = (value: string) => {
  const normalized = value.toLowerCase();
  return (
    normalized.includes('gateway timeout') ||
    normalized.includes('gateway time-out') ||
    (normalized.includes('504') &&
      (normalized.includes('gateway') || normalized.includes('timeout')))
  );
};

const getStatusFromRecord = (record: Record<string, unknown>) => {
  const status = getNumberValue(record.status);
  if (status !== undefined) return status;

  const statusText = getStringValue(record.status);
  if (statusText) {
    const parsed = Number.parseInt(statusText, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const statusCode = getNumberValue(record.statusCode);
  if (statusCode !== undefined) return statusCode;

  const statusCodeText = getStringValue(record.statusCode);
  if (statusCodeText) {
    const parsed = Number.parseInt(statusCodeText, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return undefined;
};

export const isGatewayTimeoutError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'string') {
    return hasGatewayTimeoutSignature(error);
  }

  if (error instanceof Error && hasGatewayTimeoutSignature(error.message)) {
    return true;
  }

  if (!isRecord(error)) return false;

  if (getStatusFromRecord(error) === GATEWAY_TIMEOUT_STATUS) return true;

  const response = error.response;
  if (isRecord(response)) {
    if (getStatusFromRecord(response) === GATEWAY_TIMEOUT_STATUS) return true;
  }

  const context = error.context;
  if (isRecord(context)) {
    if (getStatusFromRecord(context) === GATEWAY_TIMEOUT_STATUS) return true;
  }

  const code = getStringValue(error.code);
  if (code?.toLowerCase() === 'gateway_timeout' || code === '504') return true;

  const message = getStringValue(error.message);
  if (message && hasGatewayTimeoutSignature(message)) return true;

  const nestedError = error.error;
  if (nestedError && nestedError !== error) {
    return isGatewayTimeoutError(nestedError);
  }

  return false;
};
